import { type Scope, createContextScope } from '@radix-ui/react-context'
import { forwardRef } from 'react'
import { LinkIcon } from './components/link-icon'
import {
  iconLinkVariants,
  labelLinkVariants,
  linkVariants,
} from './link.styles'
import type { LinkProps } from './link.types'

type LinkContext = Pick<LinkProps, 'size' | 'disabled'>

const DISPLAY_NAME = 'Link'

const LinkRoot = forwardRef<HTMLAnchorElement, LinkProps>(
  (props: LinkScopedProps<LinkProps>, ref) => {
    const {
      children,
      icon,
      size,
      disabled,
      full,
      __scopeLink,
      href,
      onClick,
      ...rest
    } = props

    const linkClassName = linkVariants({
      size,
      disabled,
      full,
    })

    const handleClick = (
      event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
      if (disabled) {
        event.preventDefault()
        event.stopPropagation()
        return
      }
      if (onClick) {
        onClick(event)
      }
    }

    return (
      <LinkProvider scope={__scopeLink} size={size} disabled={disabled}>
        <a
          {...rest}
          ref={ref}
          href={disabled ? undefined : href}
          className={linkClassName}
          aria-disabled={disabled}
          role="link"
          onClick={handleClick}
        >
          <div className={labelLinkVariants()}>{children}</div>
          {icon && <LinkIcon className={iconLinkVariants()} />}
        </a>
      </LinkProvider>
    )
  }
)

LinkRoot.displayName = DISPLAY_NAME

/*
Scope Definition
*/

export type LinkScopedProps<P> = P & {
  __scopeLink?: Scope
}

const [createLinkContext] = createContextScope(DISPLAY_NAME)

export const [LinkProvider, useLinkContext]: readonly [
  ProviderType<LinkContext>,
  (consumerName: string, scope: Scope) => LinkContext,
] = createLinkContext<LinkContext>(DISPLAY_NAME)

/*
Composition Export
*/

export const Link = {
  Root: LinkRoot,
}
